import { ref } from 'vue';
import {
  connectPrinter,
  pingAgent,
  printReceipt,
  PrinterError,
  scanPrinters,
} from '../print/printer.agent';
import { testReceipt } from '../print/printer.receipts';
import {
  isPrinterConfigured,
  usePrinterSettings,
  type PrinterSettings,
} from '../print/printer.settings';
import { usePrinter } from '../print/usePrinter';
import { fetchStoreDevices } from './devices.api';
import type { DeviceLink, PdsDevice } from './types';

const DEFAULT_PRINTER_PORT = 9100;

// Partagé par l'écran des réglages : la liste vient du serveur ; l'état réseau ne se connaît qu'une fois
// l'opérateur a demandé la connexion, jamais en arrière-plan.
const devices = ref<PdsDevice[]>([]);
const status = ref<'idle' | 'loading' | 'ready' | 'error'>('idle');
const link = ref<Record<string, DeviceLink>>({});
const notice = ref('');
const testing = ref<string | null>(null);

// Un écran de préparation porte l'adresse de l'agent d'impression de son poste.
export const isAgentHost = (device: PdsDevice) =>
  device.type === 'pds' && !!device.ip;

// Une imprimante à adresse IP se joint directement par l'agent.
export const isPrinterDevice = (device: PdsDevice) =>
  device.type === 'printer' && !!device.ip;

// Ce que l'écran sait sonder : pas un terminal de paiement ni une balance, à qui l'agent parlerait en ESC/POS.
export const isProbeable = (device: PdsDevice) =>
  isAgentHost(device) || isPrinterDevice(device);

export const portOf = (device: PdsDevice) =>
  device.port ?? DEFAULT_PRINTER_PORT;

// « http://10.0.0.4:4400 » : l'adresse de l'agent que déclare un écran de préparation.
export const agentUrlOf = (device: PdsDevice) =>
  `${device.agentScheme}://${device.ip}${device.port ? `:${device.port}` : ''}`;

// La destination d'une imprimante, avec l'agent de ce poste.
const printerTarget = (
  settings: PrinterSettings,
  device: PdsDevice,
): PrinterSettings => ({
  ...settings,
  transport: 'network',
  host: device.ip ?? '',
  port: portOf(device),
});

const failure = (error: unknown) => {
  notice.value =
    error instanceof Error ? error.message : 'Opération impossible.';
};

export const useDevices = () => {
  const settings = usePrinterSettings();

  // Une seule connexion à la fois : demander la suivante coupe celle qui était établie, qu'elle réussisse ou non.
  const cutOthers = (device: PdsDevice) => {
    for (const other of devices.value) {
      if (other.id !== device.id && link.value[other.id] === 'online')
        link.value[other.id] = 'unknown';
    }
    if (settings.connectedDevice !== device.id) forget();
  };

  const forget = () => {
    settings.connectedDevice = '';
    settings.connectedAgent = '';
  };

  const markConnected = (device: PdsDevice, agentUrl = '') => {
    link.value[device.id] = 'online';
    settings.connectedDevice = device.id;
    settings.connectedAgent = agentUrl;
  };

  const markLost = (device: PdsDevice, state: DeviceLink) => {
    link.value[device.id] = state;
    if (settings.connectedDevice === device.id) forget();
  };

  // L'adresse d'agent d'un écran de préparation : celle que l'opérateur a complétée et qui a répondu, sinon celle déclarée.
  const agentUrlFor = (device: PdsDevice) =>
    settings.connectedDevice === device.id && settings.connectedAgent
      ? settings.connectedAgent
      : agentUrlOf(device);

  // Connecter un écran de préparation : l'agent répond-il à l'adresse déclarée ? Si oui, c'est l'agent de ce poste.
  const connectAgent = async (device: PdsDevice) => {
    const url = agentUrlFor(device);
    await pingAgent(url);
    settings.agentUrl = url;
    return url;
  };

  // Le même geste, à l'adresse que l'opérateur vient de compléter : rend le message d'échec, ou null si l'agent répond.
  const connectAgentAt = async (device: PdsDevice, url: string) => {
    notice.value = '';
    cutOthers(device);
    link.value[device.id] = 'checking';
    try {
      await pingAgent(url);
      settings.agentUrl = url;
      markConnected(device, url);
      return null;
    } catch (error) {
      markLost(device, 'offline');
      return error instanceof Error ? error.message : 'Connexion impossible.';
    }
  };

  const probe = async (device: PdsDevice) => {
    if (!isProbeable(device)) return;
    notice.value = '';
    cutOthers(device);
    link.value[device.id] = 'checking';
    try {
      if (isAgentHost(device))
        markConnected(device, await connectAgent(device));
      else {
        await connectPrinter(printerTarget(settings, device));
        markConnected(device);
      }
    } catch (error) {
      markLost(device, 'offline');
      failure(error);
    }
  };

  // La connexion d'une visite précédente se retrouve telle quelle. Celle d'un agent ne vaut que tant que son adresse
  // est toujours celle du poste ; celle d'un appareil disparu de la liste est oubliée.
  const restoreConnection = () => {
    const saved = devices.value.find((d) => d.id === settings.connectedDevice);
    const stillValid =
      saved &&
      (isAgentHost(saved)
        ? !!settings.connectedAgent &&
          settings.agentUrl === settings.connectedAgent
        : true);
    if (saved && stillValid) link.value[saved.id] = 'online';
    else forget();
  };

  const load = async () => {
    status.value = 'loading';
    notice.value = '';
    try {
      devices.value = await fetchStoreDevices();
      link.value = {};
      restoreConnection();
      status.value = 'ready';
    } catch {
      status.value = 'error';
    }
  };

  // L'écran ne connaît que l'agent : l'imprimante de ce poste est celle déjà choisie, sinon la seule que l'agent trouve.
  const stationPrinter = async (agentUrl: string) => {
    if (isPrinterConfigured(settings)) return;
    const found = await scanPrinters({ ...settings, agentUrl });
    if (found.length !== 1)
      throw new PrinterError(
        found.length
          ? 'Plusieurs imprimantes trouvées : choisissez celle de ce poste dans « Configurer l’imprimante ».'
          : 'Aucune imprimante trouvée par l’agent : choisissez-la dans « Configurer l’imprimante ».',
      );
    settings.transport = 'network';
    settings.host = found[0].address ?? '';
    settings.port = found[0].meta?.port ?? DEFAULT_PRINTER_PORT;
    settings.label = found[0].name;
  };

  const testPrint = async (device: PdsDevice) => {
    testing.value = device.id;
    notice.value = '';
    try {
      if (isAgentHost(device)) {
        const agentUrl = agentUrlFor(device);
        await stationPrinter(agentUrl);
        await printReceipt(
          { ...settings, agentUrl },
          testReceipt(settings.label, Date.now()),
        );
      } else {
        await printReceipt(
          printerTarget(settings, device),
          testReceipt(device.label, Date.now()),
        );
      }
      markConnected(device, isAgentHost(device) ? agentUrlFor(device) : '');
      return true;
    } catch (error) {
      // Seul un agent qui ne répond plus rend la ligne « hors ligne » ; un refus d'imprimante se lit dans le message.
      if (error instanceof PrinterError && error.kind !== 'refused')
        markLost(device, 'offline');
      failure(error);
      return false;
    } finally {
      testing.value = null;
    }
  };

  // Ce poste : l'agent déclaré pour lui, ou l'imprimante qu'il utilise.
  const isStation = (device: PdsDevice) => {
    if (isAgentHost(device)) return settings.agentUrl === agentUrlFor(device);
    return (
      isPrinterDevice(device) &&
      settings.transport === 'network' &&
      settings.host === device.ip &&
      settings.port === portOf(device)
    );
  };

  const useForStation = async (device: PdsDevice) => {
    settings.transport = 'network';
    settings.host = device.ip ?? '';
    settings.port = portOf(device);
    settings.label = device.label;
    await usePrinter().check();
    await probe(device);
  };

  return {
    devices,
    status,
    link,
    notice,
    testing,
    load,
    probe,
    connectAgentAt,
    testPrint,
    isStation,
    agentUrlFor,
    useForStation,
  };
};
